import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, Search, UserPlus, Wifi } from 'lucide-react';

import { FriendListItem } from '@/components/social/FriendListItem';
import { RigaAccountBloccati } from '@/components/social/RigaAccountBloccati';
import { RigaNotifiche } from '@/components/social/RigaNotifiche';
import { UserAvatar } from '@/components/social/UserAvatar';
import { Interruttore } from '@/components/common/Interruttore';
import { Loader } from '@/components/common/Loader';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useHaptics } from '@/hooks/useHaptics';
import { env } from '@/env';
import { useStatoTempoReale } from '@/lib/qsocials/statoTempoReale';
import { ricontrolloAmiciMs } from '@/lib/qsocials/tempoReale';
import {
  useGetMeQuery,
  useGetOnlineFriendsFeedQuery,
  useListFollowersQuery,
  useListFollowingQuery,
  useListFriendRequestsQuery,
  useListFriendsQuery,
  useRequestFriendshipMutation,
  useSearchUsersQuery,
  useUpdateShareLiveMatchesMutation,
  useUpdateShowPresenceMutation,
} from '@/services/socialApi';
import type { FriendshipDTO } from '@/types/social';
import { useListChallengesQuery } from '@/services/qduelsApi';
import type { UserSearchResult } from '@/types/social';
import { cn } from '@/lib/cn';
import { Segmented } from '@/components/common/Segmented';
import { CommunityPanel } from '@/components/hub/panels/CommunityPanel';
import { ElencoConversazioni } from '@/components/dm/ElencoConversazioni';
import { useDmNonLetti } from '@/hooks/useDmNonLetti';
import { useSfideInArrivo } from '@/hooks/useSfideInArrivo';
import { PannelloSfide } from '@/components/social/PannelloSfide';
import { useMostraSfide } from '@/hooks/useMostraSfide';

/** Minimum query length before we hit the server search endpoint. */
const MIN_SEARCH_LEN = 2;

type SubTab = 'friends' | 'followers' | 'following' | 'duels';

/**
 * Le due META' della schermata.
 *
 * Community arriva dalla griglia della Hub, dove occupava una casella per una
 * superficie che oggi non ha dati. Sta qui perche' e' l'altra faccia della
 * stessa cosa: le persone che conosci, e i posti dove si sta insieme.
 *
 * ⚠️ PROVVISORIO, e dichiarato tale. Due segmentati impilati sono un livello di
 * navigazione in piu' di quanti ne meriti una schermata: quando Community avra'
 * un contenuto vero si decidera' la forma giusta. Intanto la cosa peggiore
 * sarebbe stata lasciarla nella Hub a occupare una casella su otto.
 */
type Area = 'friends' | 'messages' | 'community';

/**
 * Friends tab — entry point for the QSocials wave-1 surface.
 *
 * Ridisegno del 2026-09-28 (I-252 → L-2418 (a)): i CONTATORI stessi sono i
 * sotto-tab — Amici · Follower · Seguiti, più Sfide in prod quando QDuels è
 * acceso (`env.featureQduelsEnabled`) — implementati come pill switcher
 * dentro la pagina, non rotte dedicate. Le richieste di amicizia non sono
 * più un sotto-tab: sono una scheda in cima al tab Amici (v. `RequestsCard`).
 *
 * Top section:
 *   - Search input. With < 2 chars it's a client-side filter over the loaded
 *     list (the active sub-tab). With ≥ 2 chars it switches to a debounced
 *     server-side discovery search (`GET /social/users/search`, Wave 2) that
 *     surfaces *any* QLASH user with an "Add friend" action — not just people
 *     already in your lists.
 *   - "Online now" pill that flips into the requests query → shows the
 *     online-friends feed. Nascosto sul tab Follower: quel feed non contiene
 *     i soli follower e lo svuoterebbe sempre.
 */
const SUB_TABS: readonly SubTab[] = ['friends', 'followers', 'following', 'duels'];

function isSubTab(value: string | null): value is SubTab {
  return value !== null && (SUB_TABS as readonly string[]).includes(value);
}

export function Friends() {
  const { t } = useTranslation('general');
  const haptics = useHaptics();
  const [searchParams] = useSearchParams();
  // Il tab «Sfide» esiste con QDuels acceso (com'era) O sotto la serratura di piattaforma (Limulo + beta, B2).
  // Fuori serratura `piattaforma` e' la costante `false` e `mostraSfide` e' `env.featureQduelsEnabled`: il tab e'
  // quello di oggi.
  const { piattaforma, mostraSfide } = useMostraSfide();

  // Deep-link support: `/friends?tab=duels` lands directly on the Sfide
  // sub-tab (used by the Hub "Sfida" button + the QueueSheet "Sfida un amico"
  // CTA). Read once on mount — the pill switcher owns the tab after that.
  // A QDuels spento (staging), `duels` non e' una destinazione valida: il
  // ripiego di ieri sceglieva 'friends' in silenzio, lasciando l'URL a
  // `?tab=duels`. Ora l'atterraggio E' su 'friends', ma il redirect esplicito
  // (sotto) toglie anche il parametro dall'URL — la differenza che un mutante
  // sul `navigate` fa fallire.
  const [tabScelto, setTab] = useState<SubTab>(() => {
    const param = searchParams.get('tab');
    if (param === 'duels' && !mostraSfide) return 'friends';
    return isSubTab(param) ? param : 'friends';
  });
  // Il tab EFFETTIVO: se la serratura cade mentre si e' su «Sfide» (il ruolo beta si rilegge dal token, e il gate
  // e' simmetrico) la pastiglia sparisce, e senza questo restavano tre pastiglie senza selezione e una pagina
  // bianca. Derivato, non scritto: lo stato resta `duels`, l'URL e la cronologia non si toccano, e se la
  // serratura (o QDuels) torna, «Sfide» torna com'era.
  const tab: SubTab = tabScelto === 'duels' && !mostraSfide ? 'friends' : tabScelto;
  const navigate = useNavigate();
  useEffect(() => {
    if (searchParams.get('tab') === 'duels' && !mostraSfide) {
      navigate('/friends', { replace: true });
    }
    // Una volta sola: e' un atterraggio, non un ricalcolo a ogni render.
  }, []);
  const [area, setArea] = useState<Area>('friends');
  // «Messaggi» e' la terza voce dello switch (L-2238 A3 (1)), col numero dei non letti di `/unread` — il
  // numero lo dice il server (R6: solo le aperte), non lo si ricalcola dall'elenco. Senza il flag dei DM la
  // voce non c'e'.
  const dmNonLetti = useDmNonLetti();
  const [query, setQuery] = useState('');
  const [onlineOnly, setOnlineOnly] = useState(false);

  // Server-side discovery search kicks in at >= 2 chars (debounced).
  const debouncedQuery = useDebouncedValue(query.trim(), 300);
  const searchActive = debouncedQuery.length >= MIN_SEARCH_LEN;
  const search = useSearchUsersQuery({ q: debouncedQuery, limit: 20 }, { skip: !searchActive });

  // Richieste e amici arrivano dal tempo reale di QSocials (`useTempoRealeQsocials`, in AppShell); il
  // ricontrollo periodico e' la rete di sicurezza (R7, 60 s) o il ripiego a collegamento giu' (R4, 30 s),
  // solo con la pagina visibile. V. `ricontrolloAmiciMs`.
  const ricontrollo = ricontrolloAmiciMs(env.qsocialsWsUrl, useStatoTempoReale());
  const friends = useListFriendsQuery(undefined, {
    refetchOnFocus: true,
    pollingInterval: ricontrollo,
    skipPollingIfUnfocused: true,
  });
  const requests = useListFriendRequestsQuery(undefined, {
    refetchOnFocus: true,
    pollingInterval: ricontrollo,
    skipPollingIfUnfocused: true,
  });
  const following = useListFollowingQuery(undefined, { refetchOnFocus: true });
  const followers = useListFollowersQuery(undefined, { refetchOnFocus: true });
  const onlineFriends = useGetOnlineFriendsFeedQuery(undefined, {
    refetchOnFocus: true,
    pollingInterval: 30_000,
    skip: !onlineOnly,
  });

  // Punto 6 del ridisegno Amici (L-2418, PR-2): il gruppo Privacy in fondo al
  // tab Amici. Il gruppo si monta SOLO con `me.data`, cioè dopo che la query
  // ha risposto: senza, la sezione non monta affatto (vedi il render sotto).
  // È il disegno, e vale anche a rotta servita.
  // `skip` = «non sono sul tab Amici dell'area Amici» (revisione indipendente
  // #492, 🟡2): il sotto-tab `tab` da solo non basta, perché resta 'friends'
  // anche quando l'area è Messaggi o Community. Lo `skip` toglie la
  // sottoscrizione fuori da area Amici + tab Amici. Oggi evita solo la GET di
  // un atterraggio diretto su Follower, Seguiti o Sfide, e ne costa una al
  // ritorno dopo 60 s (`keepUnusedDataFor`). Esiste perché un polling o un
  // `refetchOnFocus` aggiunti domani non partano lì.
  const me = useGetMeQuery(undefined, { skip: area !== 'friends' || tab !== 'friends' });
  const [updateShowPresence, updateShowPresenceState] = useUpdateShowPresenceMutation();
  const [updateShareLive, updateShareLiveState] = useUpdateShareLiveMatchesMutation();

  // Incoming duel challenges (role=defender). Polled fast while the Sfide tab
  // is open (live 5-min window), slow otherwise just to keep the badge fresh.
  // A QDuels spento (staging) il tab Sfide non esiste: niente poll a livello
  // pagina — punto 3 del ridisegno del 28/09.
  const onDuels = tab === 'duels';
  const incomingChallenges = useListChallengesQuery(
    { role: 'defender' },
    {
      pollingInterval: onDuels ? 5_000 : 20_000,
      refetchOnFocus: true,
      skip: !env.featureQduelsEnabled,
    },
  );
  const incomingPendingCount = useMemo(
    () => (incomingChallenges.data ?? []).filter((c) => c.status === 'pending').length,
    [incomingChallenges.data],
  );

  // Le sfide di piattaforma in arrivo: la cache della barra, nessun poll in piu' (`useSfideInArrivo`).
  const sfidePiattaforma = useSfideInArrivo();

  // I numeri dei tab sono i conteggi del SERVER (`total`), mai la lunghezza
  // della pagina caricata: sopra 100 la lista si ferma, il totale no (punto 1).
  const counts: Record<SubTab, number> = {
    friends: friends.data?.length ?? 0,
    followers: followers.data?.total ?? 0,
    following: following.data?.total ?? 0,
    duels: (env.featureQduelsEnabled ? incomingPendingCount : 0) + sfidePiattaforma.vive.length,
  };
  // In stato d'errore il numero non si legge («0» affermerebbe un fatto sul
  // server che non ha risposto): il canvas mostra «–», e lo mostriamo anche
  // noi (revisione indipendente #484, punto 5).
  const tabErrors: Partial<Record<SubTab, boolean>> = {
    friends: friends.isError,
    followers: followers.isError,
    following: following.isError,
    duels: (env.featureQduelsEnabled ? incomingChallenges.isError : false) || (piattaforma && sfidePiattaforma.isError),
  };
  const incomingRequestsCount = requests.data?.incoming.length ?? 0;
  const outgoingRequestsCount = requests.data?.outgoing.length ?? 0;
  // Il puntino (aria-hidden) conta SOLO le in arrivo (punto 5: il badge non
  // conta più le inviate). La SCHEDA invece compare con «almeno una
  // richiesta», in arrivo O inviata (revisione indipendente #484, punto 2):
  // senza il tab Richieste di ieri, chi ha solo inviate non le vedrebbe più
  // e non potrebbe annullarle.
  const hasAnyRequests = incomingRequestsCount + outgoingRequestsCount > 0;
  // «Segui già» su Follower (punto 8) si legge dalla lista Seguiti già
  // caricata, non da una query per riga (revisione indipendente #484, punto
  // 4): oltre il tetto di paginazione (100, non nostro) una riga non ancora
  // caricata mostra il bottone Segui, come se non si seguisse — dichiarato,
  // non misurato riga per riga.
  const followingIds = useMemo(
    () => new Set((following.data?.items ?? []).map((f) => f.userId)),
    [following.data],
  );

  const switchArea = (next: Area) => {
    if (next === area) return;
    haptics.selection();
    setArea(next);
  };

  const switchTab = (next: SubTab) => {
    // Si confronta lo stato SCELTO, non il tab effettivo: durante il ripiego (serratura caduta) un tocco su
    // «Amici» e' l'ultima scelta esplicita dell'utente e deve scriversi, o al ritorno della serratura la vista
    // salterebbe a «Sfide» contro quel tocco.
    if (next === tabScelto) return;
    haptics.selection();
    setTab(next);
  };

  const onlineSet = useMemo(() => {
    if (!onlineOnly) return null;
    const ids = onlineFriends.data?.feed.map((f) => f.userId) ?? [];
    return new Set(ids);
  }, [onlineOnly, onlineFriends.data]);

  // Client-side filter for the loaded lists, used only for short queries
  // (< 2 chars) and the "online only" toggle. At >= 2 chars the UI switches
  // to the server-side discovery search instead (searchActive). Matches on
  // displayName (now available via enriched lists) as well as raw id.
  //
  // Il filtro ONLINE non si applica MAI sul tab Follower, anche se
  // `onlineOnly` e' rimasto acceso passando da un altro tab: il feed online
  // non contiene i soli follower e li svuoterebbe tutti (punto 4) — il
  // bottone e' nascosto li', ma lo stato del toggle sopravvive al cambio tab.
  const effectiveOnlineSet = tab === 'followers' ? null : onlineSet;
  const filterByQuery = <
    T extends {
      friendUserId?: string;
      userId?: string;
      displayName?: string | null;
    },
  >(
    items: T[],
  ): T[] => {
    const q = query.trim().toLowerCase();
    if (!q && !effectiveOnlineSet) return items;
    return items.filter((item) => {
      const id = (item.friendUserId ?? item.userId) || '';
      const name = (item.displayName ?? '').toLowerCase();
      const passQ = !q || id.toLowerCase().includes(q) || name.includes(q);
      const passOnline = !effectiveOnlineSet || effectiveOnlineSet.has(id);
      return passQ && passOnline;
    });
  };

  return (
    <div className="flex flex-col gap-4 px-4 pb-6 pt-5">
      {/* Lo switch fra le due META' della schermata. Sta in cima a tutto —
          sopra le statistiche e il titolo — perche' quello che c'e' sotto
          cambia per intero: un interruttore in mezzo alla pagina si legge come
          un filtro di cio' che ha sopra, e qui non lo e'. */}
      <Segmented
        value={area}
        onChange={switchArea}
        options={[
          { value: 'friends', label: t('social.area_friends', { defaultValue: 'Friends' }) },
          ...(env.featureDmEnabled
            ? [
                {
                  value: 'messages' as const,
                  label: t('social.area_messages', { defaultValue: 'Messages' }),
                  count: dmNonLetti,
                },
              ]
            : []),
          { value: 'community', label: t('social.area_community', { defaultValue: 'Community' }) },
        ]}
      />

      {area === 'messages' && env.featureDmEnabled && <ElencoConversazioni />}

      {/* Community: i tornei della tua community (QDuels, `visibility:
          community`). Lo stesso componente che serviva la casella nella Hub —
          non una copia: il giorno che quella superficie cresce, cresce in un
          posto solo.
          ⚠️ `h-[65vh]`: il pannello porta `h-full` + 
          `overflow-hidden`, e dentro una colonna che si dimensiona sul
          contenuto quel `h-full` non ha da cosa risolversi — collasserebbe a
          zero. Nella Hub l'altezza gliela dava il pannello; qui gliela diamo
          noi, ed e' una misura provvisoria quanto la sistemazione. */}
      {area === 'community' && (
        <div className="h-[65vh]">
          <CommunityPanel />
        </div>
      )}

      {area === 'friends' && (
        <>
          {/* I contatori SONO i tab (I-252 → L-2418 (a), 2026-09-28): niente più
          barra statistiche + titolo + sotto-tab separati — le tre voci con
          indicati amici/follower/seguiti sostituiscono la selezione, perché
          fanno la stessa cosa. Il titolo «Amici» e il claim sono tolti: coi
          numeri come tab «Amici» comparirebbe tre volte (assunto dichiarato,
          v. reference KB). */}
          <AmiciTabs
            tab={tab}
            onChange={switchTab}
            counts={counts}
            errors={tabErrors}
            incomingRequestsCount={incomingRequestsCount}
            showDuels={mostraSfide}
          />

          {/* Search + online filter. ONLINE e' nascosto sul tab Follower: quel
          feed non contiene i soli follower e lo svuoterebbe sempre (punto 4). */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search
                aria-hidden
                className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-muted"
              />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('social.search_placeholder', {
                  defaultValue: 'Search players by name',
                })}
                className={cn(
                  'card-edge h-10 w-full rounded-xl bg-bg-secondary pl-9 pr-3 text-sm text-fg outline-none',
                  'placeholder:text-fg-muted focus:ring-1 focus:ring-brand',
                )}
              />
            </div>
            {tab !== 'followers' && (
              <button
                type="button"
                data-testid="online-toggle"
                onClick={() => {
                  haptics.selection();
                  setOnlineOnly((v) => !v);
                }}
                className={cn(
                  'flex h-10 items-center gap-1.5 rounded-xl px-3 text-xs font-bold uppercase tracking-wide transition-colors',
                  onlineOnly ? 'bg-brand text-brand-fg' : 'card-edge bg-bg-secondary text-fg-muted',
                )}
                aria-pressed={onlineOnly}
              >
                <Wifi className="h-4 w-4" />
                {t('social.online_now', { defaultValue: 'Online' })}
              </button>
            )}
          </div>

          {/* Discovery search results (>= 2 chars) — server-side, any QLASH user */}
          {searchActive && (
            <SearchResults
              isFetching={search.isFetching}
              isError={search.isError}
              results={search.data ?? []}
            />
          )}

          {/* Richieste + lista del tab attivo (nascosti mentre la ricerca globale e' attiva) */}
          {!searchActive && (
            <>
              {tab === 'friends' && hasAnyRequests && (
                <RequestsCard
                  incoming={requests.data?.incoming ?? []}
                  outgoing={requests.data?.outgoing ?? []}
                />
              )}

              {tab === 'friends' && (
                <ListContainer
                  isLoading={friends.isLoading}
                  isError={friends.isError}
                  onRetry={friends.refetch}
                  empty={emptyListText(
                    onlineOnly,
                    friends.data ?? [],
                    filterByQuery(friends.data ?? []),
                    t('social.empty_friends', {
                      defaultValue: 'No friends yet. Send a request from a profile.',
                    }),
                    t,
                  )}
                >
                  <ul className="flex flex-col gap-2">
                    {filterByQuery(friends.data ?? []).map((f) => (
                      <FriendListItem
                        key={f.friendUserId}
                        userId={f.friendUserId}
                        displayName={f.displayName}
                        avatarUrl={f.avatarUrl}
                        mode="friend"
                      />
                    ))}
                  </ul>
                </ListContainer>
              )}

              {tab === 'followers' && (
                <ListContainer
                  isLoading={followers.isLoading}
                  isError={followers.isError}
                  onRetry={followers.refetch}
                  empty={
                    !followers.data?.items.length
                      ? t('social.empty_followers', {
                          defaultValue: 'No followers yet.',
                        })
                      : null
                  }
                >
                  <ul className="flex flex-col gap-2">
                    {filterByQuery(followers.data?.items ?? []).map((f) => (
                      <FriendListItem
                        key={f.userId}
                        userId={f.userId}
                        displayName={f.displayName}
                        avatarUrl={f.avatarUrl}
                        mode="follower"
                        alreadyFollowing={followingIds.has(f.userId)}
                      />
                    ))}
                  </ul>
                </ListContainer>
              )}

              {tab === 'following' && (
                <ListContainer
                  isLoading={following.isLoading}
                  isError={following.isError}
                  onRetry={following.refetch}
                  empty={emptyListText(
                    onlineOnly,
                    following.data?.items ?? [],
                    filterByQuery(following.data?.items ?? []),
                    t('social.empty_following', {
                      defaultValue: "You're not following anyone yet.",
                    }),
                    t,
                  )}
                >
                  <ul className="flex flex-col gap-2">
                    {filterByQuery(following.data?.items ?? []).map((f) => (
                      <FriendListItem
                        key={f.userId}
                        userId={f.userId}
                        displayName={f.displayName}
                        avatarUrl={f.avatarUrl}
                        mode="follow"
                      />
                    ))}
                  </ul>
                </ListContainer>
              )}

              {tab === 'duels' && mostraSfide && (
                <>
                  <PannelloSfide
                    piattaforma={piattaforma}
                    incoming={incomingChallenges.data ?? []}
                    incomingLoading={incomingChallenges.isLoading}
                    incomingError={incomingChallenges.isError}
                  />
                </>
              )}

              {/* Privacy (punto 6, PR-2): solo sul tab Amici, e solo quando la
              query ha già risposto (`me.data`) — è il disegno, letto dalla
              query e non da un flag di build. */}
              {tab === 'friends' && me.data && (
                <PrivacySection
                  // «Assente» = VISIBILE (revisione indipendente #492, punto 1): il server
                  // normalizza sempre a booleano (qsocials-api `?? true`), ma il client non
                  // deve ripetere la bugia se un giorno arrivasse `undefined` — `!== false`,
                  // mai il valore grezzo.
                  showPresence={me.data.showPresence !== false}
                  saving={updateShowPresenceState.isLoading}
                  failed={updateShowPresenceState.isError}
                  onToggle={() => {
                    haptics.impact('light');
                    updateShowPresence({ showPresence: !(me.data!.showPresence !== false) });
                  }}
                  // «Fai sapere quando gioco» (contratto r38): la riga c'e' SOLO se `GET /social/me` porta il campo
                  // come booleano. Assente = «questo QSocials non ha l'interruttore» ⇒ la sezione e' com'era.
                  shareLiveMatches={
                    typeof me.data.shareLiveMatches === 'boolean' ? me.data.shareLiveMatches : null
                  }
                  shareLiveSaving={updateShareLiveState.isLoading}
                  shareLiveFailed={updateShareLiveState.isError}
                  onToggleShareLive={() => {
                    haptics.impact('light');
                    updateShareLive({ shareLiveMatches: !me.data!.shareLiveMatches });
                  }}
                />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Testo del vuoto: quando il filtro ONLINE svuota una lista che aveva righe,
 * la frase e' «Nessuno online adesso.» (punto 4) — non il vuoto generico del
 * tab, che resterebbe fuorviante («Nessun amico» mentre gli amici ci sono,
 * solo nessuno online). Il filtro ONLINE non si applica sul tab Follower
 * (bottone nascosto), quindi qui la lista grezza non e' mai stata ridotta da
 * lui in quel caso.
 */
function emptyListText<T>(
  onlineOnly: boolean,
  rawList: T[],
  filtered: T[],
  defaultEmpty: string,
  t: (k: string, o: { defaultValue: string }) => string,
): string | null {
  if (filtered.length > 0) return null;
  if (onlineOnly && rawList.length > 0) {
    return t('social.online_empty', { defaultValue: 'No one online right now.' });
  }
  return rawList.length === 0 ? defaultEmpty : null;
}

/**
 * I numeri sono i tab (I-252 → L-2418 (a)): Amici · Follower · Seguiti, con
 * Sfide in coda quando QDuels e' acceso. Selezionata = pastiglia piena
 * `bg-brand`; il puntino sta SOLO su Amici, e SOLO per le richieste in
 * ARRIVO (punto 5: il badge non conta piu' le inviate).
 */
function AmiciTabs({
  tab,
  onChange,
  counts,
  errors,
  incomingRequestsCount,
  showDuels,
}: {
  tab: SubTab;
  onChange: (t: SubTab) => void;
  counts: Record<SubTab, number>;
  /** Un tab in errore mostra «–» (canvas), mai «0»: uno zero affermerebbe un dato che il server non ha dato. */
  errors: Partial<Record<SubTab, boolean>>;
  incomingRequestsCount: number;
  showDuels: boolean;
}) {
  const { t } = useTranslation('general');
  const defs: { value: SubTab; label: string }[] = [
    { value: 'friends', label: t('social.tab_friends', { defaultValue: 'Friends' }) },
    { value: 'followers', label: t('social.tab_followers', { defaultValue: 'Followers' }) },
    { value: 'following', label: t('social.tab_following', { defaultValue: 'Following' }) },
    ...(showDuels
      ? [{ value: 'duels' as const, label: t('social.tab_duels', { defaultValue: 'Duels' }) }]
      : []),
  ];
  const hasIncomingRequests = incomingRequestsCount > 0;

  return (
    <div
      role="tablist"
      aria-label={t('social.friends_title', { defaultValue: 'Friends' })}
      className="card-edge flex gap-1 rounded-2xl bg-bg-secondary p-1"
    >
      {defs.map((d) => {
        const on = d.value === tab;
        const n = counts[d.value];
        const isFriendsTab = d.value === 'friends';
        // Il puntino e' aria-hidden (decorazione): l'informazione vera sta
        // nell'aria-label del tab, cosi' le richieste in arrivo restano
        // annunciate a chi non vede il puntino (revisione indipendente #484,
        // punto 11). `count` (non `n`): e' la chiave che i18next usa per il
        // plurale automatico (`_one`/`_other`, revisione 2ª passata, punto 2)
        // — con `n` la stessa richiesta sarebbe rimasta sempre al plurale.
        const ariaLabel =
          isFriendsTab && hasIncomingRequests
            ? t('social.tab_friends_requests_aria', {
                count: incomingRequestsCount,
                defaultValue: '{{count}} incoming requests',
              })
            : undefined;
        return (
          <button
            key={d.value}
            type="button"
            role="tab"
            aria-selected={on}
            aria-label={ariaLabel}
            data-testid={`amici-tab-${d.value}`}
            onClick={() => onChange(d.value)}
            className={cn(
              'relative flex min-w-0 flex-1 flex-col items-center gap-0.5 rounded-xl px-2 py-2 transition-colors',
              on ? 'bg-brand text-brand-fg' : 'text-fg-muted',
            )}
          >
            <span
              data-testid={`amici-tab-count-${d.value}`}
              className="text-lg font-extrabold leading-tight"
            >
              {errors[d.value] ? '–' : n}
            </span>
            <span className="truncate text-[10px] font-bold uppercase tracking-wide">
              {d.label}
            </span>
            {isFriendsTab && hasIncomingRequests && (
              <span
                aria-hidden
                data-testid="amici-tab-dot"
                className={cn(
                  'absolute right-3 top-2 h-2 w-2 rounded-full',
                  on ? 'bg-brand-fg' : 'bg-brand',
                )}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Scheda «Richieste di amicizia · N» in cima al tab Amici (punto 5): compare
 * con ALMENO UNA richiesta, in arrivo O inviata (revisione indipendente
 * #484, punto 2 — senza il vecchio tab Richieste, chi ha solo inviate non le
 * vedrebbe più e non potrebbe annullarle; il puntino sul tab resta solo per
 * le in arrivo). Il link «Inviate (N) ›» apre, DENTRO la stessa scheda, le
 * richieste inviate (con Annulla) — scelta mia perche' il canvas non disegna
 * dove va il link; si torna tappando di nuovo il link.
 */
function RequestsCard({
  incoming,
  outgoing,
}: {
  incoming: FriendshipDTO[];
  outgoing: FriendshipDTO[];
}) {
  const { t } = useTranslation('general');
  const [showOutgoing, setShowOutgoing] = useState(false);
  const rows = showOutgoing ? outgoing : incoming;

  return (
    <section
      aria-label={t('social.requests_title', { defaultValue: 'Friend requests' })}
      data-testid="requests-card"
      className="card-edge flex flex-col gap-2 rounded-2xl bg-brand/10 p-3"
    >
      <div className="flex items-center justify-between gap-2 px-1">
        {/* Con 0 in arrivo e sole inviate il numero si TOGLIE, non si mostra
            «· 0»: la scheda esiste per via delle inviate, e «· 0» avrebbe
            affermato un vuoto proprio nella riga che dice perché è aperta
            (revisione 2ª passata, punto 1 — scelta dichiarata: niente
            apertura automatica delle inviate, resta un tap sul link). */}
        <h2 className="text-[11px] font-bold uppercase tracking-wide text-brand">
          {t('social.requests_title', { defaultValue: 'Friend requests' })}
          {incoming.length > 0 ? ` · ${incoming.length}` : ''}
        </h2>
        {outgoing.length > 0 && (
          <button
            type="button"
            data-testid="requests-toggle-outgoing"
            onClick={() => setShowOutgoing((v) => !v)}
            className="shrink-0 text-xs font-semibold text-fg-muted"
          >
            {t('social.outgoing_link', {
              n: outgoing.length,
              defaultValue: 'Sent ({{n}}) ›',
            })}
          </button>
        )}
      </div>
      <ul className="flex flex-col gap-2">
        {rows.map((r) => (
          <FriendListItem
            key={r.friendUserId}
            userId={r.friendUserId}
            displayName={r.displayName}
            avatarUrl={r.avatarUrl}
            mode={showOutgoing ? 'outgoing' : 'incoming'}
            hidePresence
          />
        ))}
      </ul>
    </section>
  );
}

/**
 * Il gruppo «Privacy» in fondo al tab Amici (punto 6, PR-2 — L-2418).
 *
 * Due righe: l'interruttore «Mostra quando sono online» e «Account bloccati · N ›»,
 * che porta alla pagina del punto 7 (PR-3, `RigaAccountBloccati`).
 *
 * ⛔ Lo stato «nascosto» non si simula lato client (niente heartbeat toccato
 * qui): il toggle chiama SOLO `PATCH /social/me`, e il server pubblica il
 * diff di presenza da sé.
 */
function PrivacySection({
  showPresence,
  saving,
  failed,
  onToggle,
  shareLiveMatches,
  shareLiveSaving,
  shareLiveFailed,
  onToggleShareLive,
}: {
  showPresence: boolean;
  saving: boolean;
  /** La PATCH più recente è fallita (revisione indipendente #492, punto 2): lo switch NON
   *  mente (resta sul valore vero, nessun ottimismo da disfare) ma l'utente deve saperlo. */
  failed: boolean;
  onToggle: () => void;
  /** `null` = il server non porta il campo: la riga non si mostra. */
  shareLiveMatches: boolean | null;
  shareLiveSaving: boolean;
  shareLiveFailed: boolean;
  onToggleShareLive: () => void;
}) {
  const { t } = useTranslation('general');
  return (
    <section
      aria-label={t('social.privacy_title', { defaultValue: 'Privacy' })}
      className="card-edge flex flex-col rounded-2xl bg-bg-secondary/60"
    >
      <h2 className="px-4 pb-1 pt-3 text-[11px] font-bold uppercase tracking-wide text-fg-muted">
        {t('social.privacy_title', { defaultValue: 'Privacy' })}
      </h2>
      <div className="flex min-h-[64px] items-center gap-3 px-4 py-2">
        <div className="min-w-0 flex-1">
          <p id="show-presence-label" className="text-sm font-semibold text-fg">
            {t('social.show_presence_label', { defaultValue: "Show when I'm online" })}
          </p>
          <p className="mt-0.5 text-xs text-fg-muted">
            {showPresence
              ? t('social.show_presence_on', {
                  // Testo cambiato da Luca (L-2446 (a), 2026-09-28T23:52:45Z): non più
                  // «Others», ma chi vede online è solo la propria rete social.
                  defaultValue: "Friends and followers can see when you're online.",
                })
              : t('social.show_presence_off', {
                  defaultValue:
                    "You'll appear offline to others, even in a tournament or watching a live.",
                })}
          </p>
          {failed && (
            <p className="mt-1 text-xs text-danger" role="status">
              {t('social.show_presence_error', {
                defaultValue: "Couldn't save. Try again.",
              })}
            </p>
          )}
        </div>
        <Interruttore
          acceso={showPresence}
          aria-labelledby="show-presence-label"
          data-testid="show-presence-switch"
          disabled={saving}
          onClick={onToggle}
        />
      </div>
      {shareLiveMatches !== null && (
        <div className="flex min-h-[64px] items-center gap-3 px-4 py-2">
          <div className="min-w-0 flex-1">
            <p id="share-live-label" className="text-sm font-semibold text-fg">
              {t('social.share_live_label', { defaultValue: 'Let people know when I play' })}
            </p>
            <p className="mt-0.5 text-xs text-fg-muted">
              {!showPresence
                ? t('social.share_live_needs_presence', {
                    defaultValue: "Only works with “Show when I'm online” turned on.",
                  })
                : shareLiveMatches
                  ? t('social.share_live_on', {
                      defaultValue:
                        'When you start a QLASH match, your friends see you playing and can watch you; people who follow you may get a Telegram alert.',
                    })
                  : t('social.share_live_off', {
                      defaultValue: 'Nobody gets alerts about your matches and nobody sees you playing.',
                    })}
            </p>
            {shareLiveFailed && (
              <p className="mt-1 text-xs text-danger" role="status">
                {t('social.show_presence_error', { defaultValue: "Couldn't save. Try again." })}
              </p>
            )}
          </div>
          <Interruttore
            acceso={shareLiveMatches}
            aria-labelledby="share-live-label"
            data-testid="share-live-switch"
            disabled={shareLiveSaving || !showPresence}
            onClick={onToggleShareLive}
          />
        </div>
      )}
      <RigaAccountBloccati />
      {env.featureNotificationSettingsEnabled && <RigaNotifiche />}
    </section>
  );
}

/**
 * Discovery search results panel (Wave 2). Renders server-side
 * `GET /social/users/search` hits, each with an "Add friend" action that
 * fires `POST /social/friends/request`.
 */
function SearchResults({
  isFetching,
  isError,
  results,
}: {
  isFetching: boolean;
  isError: boolean;
  results: UserSearchResult[];
}) {
  const { t } = useTranslation('general');

  if (isFetching && results.length === 0) {
    return (
      <div className="flex h-24 items-center justify-center">
        <Loader />
      </div>
    );
  }
  if (isError) {
    return (
      <div className="card-edge rounded-xl bg-bg-secondary p-6 text-center text-sm text-danger">
        {t('social.search_failed', { defaultValue: 'Search failed.' })}
      </div>
    );
  }
  if (results.length === 0) {
    return (
      <div className="card-edge rounded-xl bg-bg-secondary p-6 text-center text-sm text-fg-muted">
        {t('social.search_empty', { defaultValue: 'No players found.' })}
      </div>
    );
  }
  return (
    <ul className="flex flex-col gap-2">
      {results.map((r) => (
        <SearchResultRow key={r.userId} result={r} />
      ))}
    </ul>
  );
}

function SearchResultRow({ result }: { result: UserSearchResult }) {
  const { t } = useTranslation('general');
  const haptics = useHaptics();
  const navigate = useNavigate();
  const [requestFriendship, { isLoading, isSuccess }] = useRequestFriendshipMutation();

  const name = result.displayName || t('social.unknown_user', { defaultValue: 'QLASH player' });

  const onAdd = () => {
    if (isLoading || isSuccess) return;
    haptics.impact('light');
    requestFriendship({ friendUserId: result.userId });
  };

  const openProfile = () => {
    haptics.selection();
    navigate(`/profile/${encodeURIComponent(result.userId)}`);
  };

  return (
    <li className="flex items-center gap-3 rounded-xl bg-bg-secondary p-3">
      <button
        type="button"
        onClick={openProfile}
        className="flex min-w-0 flex-1 items-center gap-3 text-left transition-opacity active:opacity-70"
      >
        <UserAvatar userId={result.userId} label={name} avatarUrl={result.avatarUrl} size="md" />
        <p className="min-w-0 flex-1 truncate text-sm font-semibold text-fg">{name}</p>
      </button>
      <button
        type="button"
        onClick={onAdd}
        disabled={isLoading || isSuccess}
        className={cn(
          'flex h-8 items-center gap-1.5 rounded-full px-3 text-xs font-bold uppercase tracking-wide transition-opacity active:opacity-80 disabled:opacity-60',
          isSuccess ? 'bg-bg-tertiary text-fg-muted' : 'bg-brand text-brand-fg',
        )}
        aria-label={t('social.add_friend', { defaultValue: 'Add friend' })}
      >
        {isSuccess ? (
          <>
            <Check className="h-4 w-4" />
            {t('social.request_sent', { defaultValue: 'Sent' })}
          </>
        ) : (
          <>
            <UserPlus className="h-4 w-4" />
            {t('social.add_friend', { defaultValue: 'Add' })}
          </>
        )}
      </button>
    </li>
  );
}

function ListContainer({
  isLoading,
  isError,
  onRetry,
  empty,
  children,
}: {
  isLoading: boolean;
  isError: boolean;
  /** Chiamata dal bottone «Riprova» (punto 9). Facoltativo: `ChallengesPanel` non lo usa qui. */
  onRetry?: () => void;
  empty: string | null;
  children: React.ReactNode;
}) {
  const { t } = useTranslation('general');
  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <Loader />
      </div>
    );
  }
  if (isError) {
    return (
      <div className="card-edge flex flex-col items-center gap-3 rounded-xl bg-bg-secondary p-6 text-center text-sm">
        <p className="text-danger">
          {t('social.load_error', { defaultValue: "Couldn't load the list." })}
        </p>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="rounded-full bg-bg-tertiary px-4 py-1.5 text-xs font-bold text-fg"
          >
            {t('social.retry', { defaultValue: 'Retry' })}
          </button>
        )}
      </div>
    );
  }
  if (empty) {
    return (
      <div className="card-edge rounded-xl bg-bg-secondary p-6 text-center text-sm text-fg-muted">
        {empty}
      </div>
    );
  }
  return <>{children}</>;
}
